// Unterschriftenfeld. Canvas mit Pointer-Events — funktioniert mit Finger,
// Stift und Maus gleichermaßen.
//
// Zwei Fallen, die hier gelöst sind:
//   * Die Tintenfarbe darf NICHT hart codiert sein. Vorher war sie hellgrau —
//     im hellen Theme also weiß auf weiß, die Unterschrift war unsichtbar.
//   * Der Canvas muss neu vermessen werden, wenn sich seine Größe ändert
//     (Displaydrehung im Auto). Sonst liegen die Striche versetzt zum Finger.

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/design-system/ui";

export function SignaturePad({
  onChange,
}: {
  onChange: (dataUrl: string | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const moved = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  /** Canvas an seine aktuelle Größe anpassen — vorhandene Zeichnung retten. */
  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(rect.width * dpr);
    const h = Math.round(rect.height * dpr);
    if (canvas.width === w && canvas.height === h) return;

    // Bestehende Striche sichern, bevor das Setzen von width/height sie löscht.
    const prev = canvas.width > 0 && canvas.height > 0 ? canvas.toDataURL() : null;
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // Tinte aus dem Theme: im dunklen Modus hell, im hellen Modus dunkel.
    ctx.strokeStyle = getComputedStyle(canvas).color || "#111827";

    if (prev) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = prev;
    }
  }, []);

  useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (canvasRef.current) ro.observe(canvasRef.current);
    window.addEventListener("orientationchange", resize);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", resize);
    };
  }, [resize]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current!.getContext("2d")!;
    // Farbe bei jedem Strich frisch lesen — der Nutzer kann das Theme
    // zwischendurch umschalten.
    ctx.strokeStyle = getComputedStyle(canvasRef.current!).color || "#111827";
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawing.current = true;
    moved.current = false;
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault(); // sonst scrollt die Seite beim Unterschreiben weg
    const ctx = canvasRef.current!.getContext("2d")!;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    moved.current = true;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    // Ein reiner Tipp ohne Bewegung ist keine Unterschrift — sonst würde ein
    // versehentlicher Fingerkontakt als "unterschrieben" durchgehen.
    if (!moved.current) return;
    onChange(canvasRef.current!.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <div className="relative overflow-hidden rounded-xl border border-ink-700 bg-ink-900">
        {/* text-ink-100 färbt die Tinte: Canvas liest seine eigene color. */}
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
          className="h-40 w-full touch-none text-ink-100"
        />
        {!hasInk && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-ink-500">
            Hier unterschreiben
          </span>
        )}
      </div>
      {hasInk && (
        <div className="mt-2 flex justify-end">
          <Button size="sm" variant="ghost" onClick={clear}>
            Löschen
          </Button>
        </div>
      )}
    </div>
  );
}
